import { Score, TeamReference } from "@/models/pdfData";
import { millisToTimeRemaining } from "@/services/dateTimeService";
import { mapFind } from "@/utils/find-map";
import {
  GameProgress,
  LastTimeout,
  SetStat,
  Substitutions,
  Teams,
} from "@/models/gameState";
import {
  GenericUpdateAction,
  UpdateAction,
} from "@/models/socketIo/updateAction";
import { getPlayerFromTeam } from "@/services/gameZustandUpdaters";
import { TeamSquads } from "@/models/gameGeneral";
import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  ChevronLeft,
  ChevronRight,
  Timer,
} from "@mui/icons-material";

export type PotentialRenderParams = {
  currentTimestamp: number;
  currentSet?: SetStat;
  currentScore?: Score;
  lastTimeout?: LastTimeout;
  lastAction?: UpdateAction;
  teams: Teams;
  teamSquads: TeamSquads;
  serving?: TeamReference;
  gameProgress?: GameProgress;
  isSetPauseOngoing?: boolean;
  expectedNextSetStartTime?: number;
  startTime?: number;
  leftTeam?: TeamReference;
  rightTeam?: TeamReference;
  substitutions?: Substitutions;
};
export type PotentialRenderResult =
  | [boolean, () => string | ReactNode]
  | [false];
export type PotentialRenderFunction = (
  params: PotentialRenderParams,
) => PotentialRenderResult;

export type ContentMap = {
  [key in string]: {
    priority: number;
    potentialRender: PotentialRenderFunction;
  };
};

export const getContent: (
  params: PotentialRenderParams & { contentMap: ContentMap },
) => string | ReactNode = ({ contentMap, ...renderParams }) => {
  const contentProvider = mapFind(
    Object.values(contentMap).sort(
      ({ priority: prioA }, { priority: prioB }) => prioA - prioB,
    ),
    ({ potentialRender }) =>
      potentialRender({
        ...renderParams,
      }),
    (result) => result[0],
  );
  if (contentProvider) {
    return contentProvider[1]?.() || "";
  } else {
    return "";
  }
};

export const renderTimeout: PotentialRenderFunction = ({
  currentTimestamp,
  lastTimeout,
  leftTeam,
  rightTeam,
  currentScore,
}) => {
  if (lastTimeout) {
    const { timeoutEndTimestamp, timeDelta, teamCode } = lastTimeout;

    if (isTimeoutOngoing(lastTimeout, currentTimestamp - 1000, currentScore)) {
      const millisRemaining = Math.min(
        timeoutEndTimestamp - timeDelta - currentTimestamp + 1000,
        30999,
      );
      const flooredSecondsRemaining = Math.floor(millisRemaining / 1000);

      return [
        true,
        () => (
          <Box
            display={"grid"}
            gridTemplateColumns={"1fr auto 1fr"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={"2vw"}
            sx={{
              "@keyframes Pulsate": {
                from: { opacity: 1 },
                to: { opacity: 0.1 },
              },
            }}
          >
            <Box
              sx={{
                flexShrink: leftTeam === teamCode ? 0 : 1,
                display: leftTeam === teamCode ? "flex" : "none",
                alignItems: "center",
                flexDirection: "row-reverse",
                justifySelf: "flex-end",
                gridColumn: "1 / 2",
                animation:
                  flooredSecondsRemaining > 5
                    ? "Pulsate 1s infinite"
                    : undefined,
              }}
            >
              {Array.from(Array(5)).map((v, i) => (
                <ChevronRight
                  sx={{
                    fontSize: "8vw",
                    marginX: "-2vw",
                    opacity: flooredSecondsRemaining > i ? 1 : 0,
                  }}
                  key={i}
                />
              ))}
            </Box>
            <Box
              display={"flex"}
              alignItems={"center"}
              gridColumn={"2 / 3"}
              gap={"2vw"}
            >
              <Timer sx={{ fontSize: "4vw" }} />
              <Typography fontSize={"5vw"}>
                {`${millisToTimeRemaining(millisRemaining)}`}
              </Typography>
              <Timer sx={{ fontSize: "4vw" }} />
            </Box>
            <Box
              sx={{
                flexShrink: rightTeam === teamCode ? 0 : 1,
                display: rightTeam === teamCode ? "flex" : "none",
                alignItems: "center",
                justifySelf: "flex-start",
                gridColumn: "3 / 4",
                animation:
                  flooredSecondsRemaining > 5
                    ? "Pulsate 1s infinite"
                    : undefined,
              }}
            >
              {Array.from(Array(5)).map((v, i) => (
                <ChevronLeft
                  sx={{
                    fontSize: "8vw",
                    marginX: "-2vw",
                    opacity: flooredSecondsRemaining > i ? 1 : 0,
                  }}
                  key={i}
                />
              ))}
            </Box>
          </Box>
        ),
      ];
    }
  }
  return [false];
};

const isTimeoutOngoing = (
  lastTimeout: LastTimeout | undefined,
  currentTimestamp: number,
  currentScore?: Score,
) =>
  !!lastTimeout &&
  lastTimeout.timeoutEndTimestamp - lastTimeout.timeDelta > currentTimestamp &&
  !lastTimeout.cancelled &&
  areScoresEqual(currentScore, lastTimeout.score);

const areScoresEqual = (
  score1: Score | undefined,
  score2: Score | undefined,
) => {
  if (!score1 || !score2) {
    return false;
  }
  return score1.team1 === score2.team1 && score1.team2 === score2.team2;
};

export const renderSetBreak: PotentialRenderFunction = ({
  isSetPauseOngoing,
  expectedNextSetStartTime,
  currentTimestamp,
  currentScore,
}) => {
  if (
    isSetPauseOngoing ||
    (expectedNextSetStartTime &&
      expectedNextSetStartTime > currentTimestamp &&
      currentScore?.team1 === 0 &&
      currentScore?.team2 === 0)
  ) {
    return [
      true,
      () => {
        if (expectedNextSetStartTime) {
          return `Satzpause: ${millisToTimeRemaining(expectedNextSetStartTime - currentTimestamp)}`;
        } else {
          return `Satzpause`;
        }
      },
    ];
  }
  return [false];
};

export const renderGameStart: PotentialRenderFunction = ({
  currentTimestamp,
  startTime,
}) => {
  if (!!startTime && startTime > currentTimestamp) {
    return [
      true,
      () =>
        `Spielbeginn: ${millisToTimeRemaining(
          startTime - currentTimestamp + 999,
        )}`,
    ];
  }
  return [false];
};

export const renderGameResult: PotentialRenderFunction = ({
  teams,
  gameProgress,
  serving,
}) => {
  if (gameProgress && gameProgress >= GameProgress.GAME_FINISHED && serving) {
    return [true, () => `Spielgewinn: ${teams?.[serving]?.name}`];
  }
  return [false];
};

export const renderSubstitution: PotentialRenderFunction = ({
  lastAction,
  teamSquads,
  leftTeam,
  rightTeam,
  substitutions,
  currentScore,
}) => [
  lastAction?.type === "[Lineups] Substitution",
  () => {
    const { teamCode, playerOutId, playerInId } = (
      lastAction as GenericUpdateAction<"[Lineups] Substitution">
    ).payload;
    const [playerOut, playerIn] = [
      getPlayerFromTeam(playerOutId, teamSquads[teamCode]),
      getPlayerFromTeam(playerInId, teamSquads[teamCode]),
    ];

    const numberOfSubstitutionsInBreak =
      substitutions?.[teamCode].filter(
        (substitution) => substitution.score === currentScore,
      ).length || 1;

    return (
      <Box
        display={"grid"}
        gridTemplateColumns={"1fr 4fr 1fr"}
        justifyContent={"space-around"}
        alignItems={"center"}
        width={"100%"}
        columnGap={"2vw"}
      >
        <Box
          sx={{
            opacity: leftTeam === teamCode ? 1 : 0,
            display: "flex",
            minWidth: 0,
            alignItems: "center",
            justifyContent: "flex-start",
            paddingLeft: "1vw",
          }}
        >
          {Array.from(Array(numberOfSubstitutionsInBreak)).map((v, i) => (
            <ChevronLeft sx={{ fontSize: "8vw", marginX: "-3vw" }} key={i} />
          ))}
        </Box>
        <Box
          display={"grid"}
          gridTemplateColumns={"repeat(4, 1fr)"}
          width={"min-content"}
          justifySelf={leftTeam === teamCode ? "left" : "right"}
          alignItems={"center"}
        >
          <Typography
            fontSize={"5vw"}
            minWidth={"9vw"}
            sx={{
              textDecoration: playerIn?.isTeamCaptain ? "underline" : "none",
            }}
          >
            #{playerIn?.jerseyNumber}
          </Typography>
          <ArrowUpward sx={{ ml: "1vw", fontSize: "6vw" }} />
          <ArrowDownward sx={{ mr: "1vw", fontSize: "4.5vw" }} />
          <Typography
            fontSize={"4vw"}
            minWidth={"7vw"}
            sx={{
              textDecoration: playerOut?.isTeamCaptain ? "underline" : "none",
            }}
          >
            #{playerOut?.jerseyNumber}
          </Typography>
        </Box>
        <Box
          sx={{
            opacity: rightTeam === teamCode ? 1 : 0,
            flexShrink: rightTeam === teamCode ? 0 : 1,
            display: "flex",
            minWidth: 0,
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "1vw",
          }}
        >
          {Array.from(Array(numberOfSubstitutionsInBreak)).map((v, i) => (
            <ChevronRight sx={{ fontSize: "8vw", marginX: "-3vw" }} key={i} />
          ))}
        </Box>
      </Box>
    );
  },
];
